import { isTrackRunning, isTrackStalled, STALLED_AFTER_DAYS } from '../../api/selectors'
import { formatDay, formatStartDay } from '../../format'
import { AdvancedCareTeamMember, AdvancedTrackDefinition, AdvancedTrackSummary } from '../../api/types'

// `intake` and `scheduled` are opened but not running yet (ADR-097).
export type TrackStatus = 'intake' | 'scheduled' | 'active' | 'stalled' | 'completed' | 'closed'

export type TrackStartOption = {
  track: AdvancedTrackDefinition
  replaces: AdvancedTrackSummary | null
}

export const findTrack = (catalog: AdvancedTrackDefinition[], trackKey: string): AdvancedTrackDefinition | undefined =>
  catalog.find((track) => track.trackKey === trackKey)

const moduleOf = (catalog: AdvancedTrackDefinition[], trackKey: string): string | null =>
  findTrack(catalog, trackKey)?.module ?? null

const activeTracks = (tracks: AdvancedTrackSummary[]) => tracks.filter((track) => track.endedAt === null)

export const trackTitle = (catalog: AdvancedTrackDefinition[], trackKey: string): string =>
  findTrack(catalog, trackKey)?.title ?? trackKey

// A null `endReason` on an ended track predates outcomes; it reads as closed, not completed.
export const trackStatus = (track: AdvancedTrackSummary, stalledAfterDays = STALLED_AFTER_DAYS): TrackStatus => {
  if (track.endedAt) {
    return track.endReason === 'completed' ? 'completed' : 'closed'
  }
  if (track.stage === 'intake') {
    return 'intake'
  }
  if (!isTrackRunning(track)) {
    return 'scheduled'
  }

  return isTrackStalled(track, stalledAfterDays) ? 'stalled' : 'active'
}

const STATUS_LABELS: Record<TrackStatus, (track: AdvancedTrackSummary) => string> = {
  intake: (track) => `Intake opened ${formatDay(track.openedAt)}`,
  scheduled: (track) => `Starts ${formatStartDay(track.startedAt)}`,
  active: (track) => `Active since ${formatStartDay(track.startedAt)}`,
  stalled: (track) => `Stalled since ${formatStartDay(track.startedAt)}`,
  completed: (track) => `Completed ${formatDay(track.endedAt)}`,
  closed: (track) =>
    track.endReason === 'discontinued'
      ? `Closed without completion ${formatDay(track.endedAt)}`
      : `Ended ${formatDay(track.endedAt)}`,
}

export const trackStatusLabel = (track: AdvancedTrackSummary, stalledAfterDays = STALLED_AFTER_DAYS): string =>
  STATUS_LABELS[trackStatus(track, stalledAfterDays)](track)

// Mirrors the service's one-per-module rule so a menu offers a transition, not a start it would refuse (ADR-090).
export const trackStartOptions = (
  catalog: AdvancedTrackDefinition[],
  tracks: AdvancedTrackSummary[]
): TrackStartOption[] => {
  const active = activeTracks(tracks)

  return catalog
    .filter((definition) => !active.some((track) => track.trackKey === definition.trackKey))
    .map((definition) => ({
      track: definition,
      replaces: definition.module
        ? active.find((track) => moduleOf(catalog, track.trackKey) === definition.module) ?? null
        : null,
    }))
}

export const switchTargets = (
  catalog: AdvancedTrackDefinition[],
  track: AdvancedTrackSummary
): AdvancedTrackDefinition[] => {
  const module = moduleOf(catalog, track.trackKey)

  return module
    ? catalog.filter((definition) => definition.module === module && definition.trackKey !== track.trackKey)
    : []
}

// Mirrors the service's `resolveLead` default: the longest-serving care-team member in the owner role (ADR-093).
export const defaultLeadFor = (
  track: AdvancedTrackDefinition | undefined,
  careTeam: AdvancedCareTeamMember[]
): string | null => {
  if (!track?.ownerRole) {
    return null
  }
  const [owner] = careTeam
    .filter((member) => member.role === track.ownerRole)
    .sort((a, b) => a.startedAt.localeCompare(b.startedAt))

  return owner?.providerUid ?? null
}
